/**
 * User Menu Component
 *
 * Displays logged-in user's info and account menu dropdown.
 * Shows login/signup buttons if not authenticated.
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import MaterialIcon from '../MaterialIcon';
import navigationService from '../../services/navigationService';
import featureFlags from '../../config/featureFlags';
import './UserMenu.css';

const UserMenu = ({ onLoginClick, onSignupClick }) => {
  const { user, isAuthenticated, isSubscriber, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
  };

  // 🚧 Feature Flag: Hide user menu if shopping cart is disabled
  if (!featureFlags.enableShoppingCart) {
    return null;
  }

  // If not authenticated, show login/signup buttons
  if (!isAuthenticated) {
    return (
      <div className="user-menu-guest">
        <button className="user-menu-login-btn" onClick={onLoginClick}>
          Log In
        </button>
        <button className="user-menu-signup-btn" onClick={onSignupClick}>
          Sign Up
        </button>
      </div>
    );
  }

  // Authenticated user menu
  const initials =
    user?.first_name && user?.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
      : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className="user-menu" ref={menuRef}>
      {/* User Avatar Button */}
      <button
        className="user-menu-button"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="User menu"
        aria-expanded={menuOpen}
      >
        <div className="user-menu-avatar">{initials}</div>
        {isSubscriber && (
          <div className="user-menu-subscriber-badge" title="Active Subscriber">
            <MaterialIcon icon="verified" />
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {menuOpen && (
        <div className="user-menu-dropdown">
          {/* User Info Header */}
          <div className="user-menu-header">
            <div className="user-menu-avatar-large">{initials}</div>
            <div className="user-menu-info">
              <div className="user-menu-name">
                {user?.first_name && user?.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : 'User'}
              </div>
              <div className="user-menu-email">{user?.email}</div>
              {isSubscriber && (
                <div className="user-menu-badge">
                  <MaterialIcon icon="verified" />
                  <span>Subscriber</span>
                </div>
              )}
            </div>
          </div>

          <div className="user-menu-divider" />

          {/* Menu Items */}
          <div className="user-menu-items">
            {/* Account Dashboard */}
            <button
              className="user-menu-item"
              onClick={() => {
                setMenuOpen(false);
                navigationService.navigateTo('account');
              }}
            >
              <MaterialIcon icon="account_circle" />
              <span>My Account</span>
            </button>

            {/* Orders */}
            <button
              className="user-menu-item"
              onClick={() => {
                setMenuOpen(false);
                navigationService.navigateTo('orders');
              }}
            >
              <MaterialIcon icon="receipt_long" />
              <span>My Orders</span>
            </button>

            {/* Subscription (if subscriber) */}
            {isSubscriber && (
              <button
                className="user-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  navigationService.navigateTo('account');
                }}
              >
                <MaterialIcon icon="card_membership" />
                <span>Subscription</span>
              </button>
            )}

            {/* Saved Scenes */}
            <button
              className="user-menu-item"
              onClick={() => {
                setMenuOpen(false);
                navigationService.navigateTo('scenes');
              }}
            >
              <MaterialIcon icon="collections" />
              <span>Saved Scenes</span>
            </button>

            {/* Design Voting (if subscriber) */}
            {isSubscriber && (
              <button
                className="user-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  // TODO: Navigate to design voting
                  console.log('Navigate to design voting');
                }}
              >
                <MaterialIcon icon="how_to_vote" />
                <span>Design Voting</span>
              </button>
            )}

            {/* Scene Contest (if subscriber) */}
            {isSubscriber && (
              <button
                className="user-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  // TODO: Navigate to scene contest
                  console.log('Navigate to scene contest');
                }}
              >
                <MaterialIcon icon="emoji_events" />
                <span>Scene Contest</span>
              </button>
            )}
          </div>

          <div className="user-menu-divider" />

          {/* Logout */}
          <div className="user-menu-items">
            <button className="user-menu-item user-menu-item-danger" onClick={handleLogout}>
              <MaterialIcon icon="logout" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

UserMenu.propTypes = {
  onLoginClick: PropTypes.func.isRequired,
  onSignupClick: PropTypes.func.isRequired,
};

export default UserMenu;
